import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper, RowModel } from "@tanstack/react-table";

import PagedTable from "../../components/shared/Tables/PagedTable";
import ClickToCopy from "../../components/shared/ClickToCopy";
import { PageMode } from "../../models/PagedTable";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import { PartStatus } from "../../models/Part";
import useCoreMutations from "../../data/useCoreMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ButtonGroup from "../shared/ButtonGroup";
import PriceFormat from "../../utils/priceFormat";
export interface ICoreOrderPartVehicle {
  id: string;
  vrm: string;
  dvlaMake: string;
  dvlaModel: string;
}

export interface ICoreOrderPartRow {
  id: string;
  tagNumber?: string;
  enteredPrice?: number;
  categoryName?: string;

  partStatus?: PartStatus;
  
  vehicle: ICoreOrderPartVehicle;
}

export interface ICoreOrderRow {
  id: string;

  kType?: number | null;
  regNo?: string | null;
  salesOrderNumber?: string | null;
  totalItems?: number | null;
  totalOrder?: string | null;
  userName?: string | null;
  completedDate?: string | null;
  created?: string; // if your API returns ISO string
  updated?: string | null;

  parts?: ICoreOrderPartRow[];
}

const colHelper = createColumnHelper<ICoreOrderRow>();

export default function CoreOrdersTable() {
  const { t } = useTranslation("stock");
  const { bulkCompleteOrders } = useCoreMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Purchase Order"));

  const [tab, setTab] = useState<"active" | "complete">("active");

  const options = useMemo(() => [
    { id: "active"  , label: t("Active Orders")  , value: "active"   },
    { id: "complete", label: t("Complete Orders"), value: "complete" },
  ], [t]);

  const selectedIndex = tab === "active" ? 0 : 1;

  const cols = useMemo(
    () => [
      TableCheckboxCol(true),
      colHelper.accessor("salesOrderNumber", {
        header: t("Sales Order"),
        sortingFn: "auto",
        cell: (info) => (
          <div className="flex items-center gap-2">
            {info.getValue() && <ClickToCopy text={info.getValue() as string} />}
          </div>
        ),
      }),
      colHelper.accessor("regNo", {
        header: t("Reg / VIN (as returned)"),
        sortingFn: "auto",
        cell: (info) => (
          <div className="flex items-center gap-2">
            {info.getValue() && <ClickToCopy text={info.getValue() as string} />}
          </div>
        ),
      }),
      colHelper.accessor("kType", {
        header: t("KType"),
        sortingFn: "auto",
      }),
      colHelper.accessor("totalItems", {
        header: t("Total Items"),
        sortingFn: "auto",
      }),
      colHelper.accessor("totalOrder", {
        header: t("Total Order"),
        sortingFn: "auto",
      }),
      colHelper.accessor("userName", {
        header: t("User"),
        sortingFn: "auto",
      }),
      colHelper.accessor("created", {
        header: t("Created"),
        sortingFn: "auto",
        cell: (info) => info.getValue() ? new Date(info.getValue() as string).toLocaleString() : "",
      }),
    ],
    [t]
  );

  return (
    <>
      <ButtonGroup
        options={options}
        selectedIndex={selectedIndex}
        onChange={(id) => setTab(id as "active" | "complete")}
      />

      <PagedTable
        key="core-orders"
        url="/rs/orders"
        columns={cols}
        pageMode={PageMode.Stock}
        queryLabel={t("Search order no, reg, user, totals, ktype…")}
        showTotal={true}
        additionalBody={tab == "active" ? {} : { completedDateIsNull : null, completedDateIsNot: { fieldName : "completedDate", value: null } }}
        initialSort={[{ id: "created", desc: true }]}
        actionButtons={[
          { label: t("Complete"), onClick: (rows: RowModel<ICoreOrderRow>) => {
            var orderIds = rows.flatRows.map(row => row.original.id);
            bulkCompleteOrders.mutate({ ids: orderIds}, {
              onSuccess: (res: any) => {
                saveHandlers.onSuccess();
              },
              onError: (res: any) => {
                saveHandlers.onError();
              }
            });
          }, disabledOnNoRows: true }
        ]}
        rowColor={(row: ICoreOrderRow) => {
          return row.completedDate == null ? "" : "bg-green-100 group-hover:!bg-green-200"
        }}
        expandedRow={(order) => (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-gray-900">{t("Parts")}</p>
              <p className="text-sm text-gray-500">
                {t("{{count}} item(s)", { count: order.parts?.length ?? 0 })}
              </p>
            </div>

            {!order.parts || order.parts.length === 0 ? (
              <div className="text-sm text-gray-500">{t("No parts linked to this order yet.")}</div>
            ) : (
              <div className="space-y-2">
                {order.parts.map((p) => (
                  <div
                    key={p.id}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-200 ${p.partStatus == PartStatus.Sold ? "bg-green-100" : "bg-gray-50"}  px-3 py-2`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {p.tagNumber && <ClickToCopy text={p.tagNumber} />}
                        {p.categoryName && (
                          <span className="text-xs rounded-full bg-white border border-gray-200 px-2 py-0.5 text-gray-700">
                            {p.categoryName}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-600 mt-1">
                        {(p.vehicle?.dvlaMake || p.vehicle?.dvlaModel) && (
                          <span>{p.vehicle?.dvlaMake} {p.vehicle?.dvlaModel}</span>
                        )}
                        {p.vehicle?.vrm && (
                          <span className="ml-2">
                            <span className="text-gray-400">•</span>{" "}
                            <ClickToCopy text={p.vehicle.vrm} />
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 text-sm text-gray-800">
                      {typeof p.enteredPrice === "number" ? PriceFormat(p.enteredPrice) : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      />
    </>
  );
}
